import { env } from "@/lib/env";

/**
 * Which salon this page is. Read from the address the customer followed and from nothing else.
 *
 * The staff app lets someone type a workspace, because a receptionist may work for a salon whose
 * address they have not bookmarked. A customer never chooses: they tapped a link in a message or
 * scanned a code on the counter, and the salon is whichever one that address belongs to. Offering a
 * picker here would be offering to show them another salon's catalogue, which is the one thing this
 * product is not (TDD-SA FR-SA-001, D1).
 *
 * Three shapes are supported. Under the platform's own domain the first label is the slug, so
 * `naturals.simpro.app` is `naturals`. On a salon's own domain, such as `book.naturals.in`, there is
 * no slug in the host at all: this returns "", the client then sends no `X-Tenant` header, and the
 * API resolves the salon from the host itself once a platform administrator has verified that domain
 * (FR-SA-006, runbooks/custom-domain-setup.md). An unverified host resolves to nothing, so the page
 * fails closed. Development and preview builds, served from localhost, use the `tenant` query
 * parameter.
 *
 * `remembered` is the slug this origin was last given. Only the query parameter needs it: the app's
 * own links drop `?tenant=`, so reloading `/my-bookings` on localhost would otherwise name no salon
 * and every call would fail with `tenant.missing`. A salon's own host only ever remembers itself, so
 * this cannot point one salon's page at another.
 */
export function resolveTenantSlug(hostname: string, search = "", remembered = ""): string {
  const base = env.baseDomain.trim().toLowerCase();
  const host = hostname.toLowerCase();

  if (base && host.endsWith(`.${base}`)) {
    const label = host.slice(0, -(base.length + 1));
    if (label && !label.includes(".") && label !== "www") return label;
  }

  // Development, previews and tests, where every salon is served from localhost.
  const fromQuery = new URLSearchParams(search).get("tenant")?.trim().toLowerCase() ?? "";
  return fromQuery || remembered.trim().toLowerCase();
}
